import { memo, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import {
  QuestionnaireBuilderView,
  useQuestionnaireBuilder,
} from "../../../../modules/questionnaire-builder";

import type { QuestionnaireBuilderLocationState } from "./types";

/**
 * State opcional injetado na navegação (ex.: `navigate("/questionnaireBuilder", { state: { questionnaireId } })`).
 * Alinhado ao padrão de outras páginas MVP que recebem contexto via `useLocation().state`
 * (ex.: PatientRecordsPage, fluxo com `navigate(..., { state: { ... } })` em useAppointmentActions).
 */
const QuestionnaireBuilderPage = memo(
  ({ organizationId }: { organizationId?: string }) => {
    const { id: questionnaireIdFromParams } = useParams<{ id?: string }>();
    const { state } = useLocation();
    const locationState =
      (state as QuestionnaireBuilderLocationState | null | undefined) ??
      undefined;

    const questionnaireIdFromState = useMemo(() => {
      if (!locationState || typeof locationState !== "object") return undefined;
      return (
        locationState.questionnaireId?.trim() ||
        locationState.id?.trim() ||
        undefined
      );
    }, [locationState]);

    const questionnaireId = useMemo(
      () => questionnaireIdFromState ?? undefined,
      [questionnaireIdFromState],
    );

    const isManageEditFlow = Boolean(
      locationState?.fromQuestionnaireManage && questionnaireId,
    );

    const navigate = useNavigate();

    const handleCloseNextStepsDialog = useCallback(() => {
      navigate("/questionnaireManagement");
    }, [navigate]);

    const hookResult = useQuestionnaireBuilder({
      organizationId: organizationId ?? undefined,
      questionnaireId,
      isManageEditFlow,
      onCloseNextStepsDialog: handleCloseNextStepsDialog,
    });

    return <QuestionnaireBuilderView {...hookResult} />;
  },
);

QuestionnaireBuilderPage.displayName = "QuestionnaireBuilderPage";

export default QuestionnaireBuilderPage;
